import { get } from 'lodash'
import React from 'react'

import Loader from '@/utilities/client-components/components/Loader/Loader'
import NotificationErrorBoundary from '@/utilities/client-components/components/NotificationErrorBoundary'

import ActionSet from './ActionSet'
import BaseAdaptiveCard from './BaseAdaptiveCard'
import ComponentRegistry from './ComponentRegistry'
import DeletedOverlay from './DeletedOverlay'
import ErrorAdaptiveCard from './ErrorAdaptiveCard'
import Footer from './Footer'
import Header from './Header'
import InViewFrame from './InViewFrame'
import NewFlag from './NewFlag'
import NotificationInteractionIndicator from './NotificationInteractionIndicator'
import PendingInline from './PendingInline'
import StyledAdaptiveCard, { StyledAdaptiveCardBody, StyledLoadingWrapper } from './StyledAdaptiveCard'

/**
 * Basically a black list of states that would make an adaptive card unusable
 * This is handy when you want to know if the card should be swipable or can view details
 * @param notification
 * @return {boolean}
 */
export const isInteractable = (notification: any) => {
  const nonInteractiveStatuses = ['pending', 'error', 'submitted', 'deleted']
  return !nonInteractiveStatuses.includes(get(notification, 'status'))
}

interface AdaptiveCardProps {
  actionHandler(...args: unknown[]): unknown
  card: object
  disableActions?: boolean
  document?: object
  inViewParent?: any
  loading?: boolean
  notification: object
  onCardHasBecomeVisible?(...args: unknown[]): unknown
  onRemove?(...args: unknown[]): unknown
  priority?: string
  selected?: boolean
}

// @ts-expect-error
class AdaptiveCard extends BaseAdaptiveCard<AdaptiveCardProps> {
  isNew: any
  isStillInView: any
  ref: any
  visibilityChangedTimer: any
  constructor(props: any) {
    super(props)

    this.isStillInView = false
    // Keep this out of state since the flag should remain after it's been viewed (only remove on refresh)
    this.isNew = !props.notification.viewed
    this.ref = React.createRef()
  }

  onSelectAction = (e: any) => {
    // Kill this if the event target is an anchor tag or we are loading
    // @ts-expect-error
    if (e.target.tagName.toLowerCase() === 'a' || this.props.loading || e.target.tagName.toLowerCase() === 'input') {
      return
    }

    // Kill this if it's not interactable (yes it's a word now)
    // TODO make 'loading' a status as well
    // @ts-expect-error
    if (!isInteractable(this.props.notification)) {
      return
    }

    // @ts-expect-error
    const { card } = this.props
    if (!get(card, 'selectAction.type')) {
      return
    }

    switch (card.selectAction.type) {
      case 'Action.OpenUrl': {
        // @ts-expect-error
        return this.actionHandler(card.selectAction.type, card.selectAction.title, { url: card.selectAction.url })
      }
      case 'Action.ShowCard': {
        // @ts-expect-error
        return this.actionHandler(card.selectAction.type, card.selectAction.title, card.selectAction.card)
      }
      case 'Action.Execute': {
        // @ts-expect-error
        return this.actionHandler(card.selectAction.type, card.selectAction.title)
      }
      default: {
        break
      }
    }
  }

  cardVisibilityChanged = (inView: any) => {
    // @ts-expect-error
    const { notification } = this.props
    clearTimeout(this.visibilityChangedTimer)

    this.isStillInView = inView

    if (!notification.viewed && inView) {
      this.visibilityChangedTimer = setTimeout(() => {
        if (this.isStillInView) {
          // @ts-expect-error
          this.props.onCardHasBecomeVisible(this.props.cardId)
        }
      }, 1000)
    }
  }

  async componentDidUpdate(prevProps: any) {
    if (
      prevProps.notification.displayLifecycle === 'detailed' &&
      prevProps.notification.status !== 'pending' &&
      // @ts-expect-error
      this.props.notification.status === 'pending'
    ) {
      // TODO animate the vanish
      // await this.animateHeightTo(0)
      // @ts-expect-error
    } else if (prevProps.notification.status !== 'submitted' && this.props.notification.status === 'submitted') {
      // @ts-expect-error
      this.props.onSuccess(this.props.notification)
      // @ts-expect-error
    } else if (prevProps.notification.status !== 'error' && this.props.notification.status === 'error') {
      // @ts-expect-error
      this.props.onError(this.props.notification.error)
    }
  }

  shouldComponentUpdate(nextProps: any) {
    if (
      // @ts-expect-error
      nextProps.selected !== this.props.selected ||
      // @ts-expect-error
      nextProps.loading !== this.props.loading ||
      // @ts-expect-error
      nextProps.notification.status !== this.props.notification.status
    ) {
      return true
    }
    return false
  }

  renderLoading = () => {
    return (
      <StyledLoadingWrapper>
        <Loader />
      </StyledLoadingWrapper>
    )
  }

  onRemove = (e: any) => {
    // @ts-expect-error
    const { onRemove, notification } = this.props
    e.stopPropagation()
    onRemove(notification)
  }

  renderDeleted = () => {
    return <DeletedOverlay onRemove={this.onRemove} />
  }

  render() {
    // @ts-expect-error
    const { card, notification, loading, document, selected, priority, inViewParent, disableActions } = this.props

    if (notification.status === 'error') {
      return <ErrorAdaptiveCard error={notification.error} />
    }

    const hasActions = card.actions && card.actions.length > 0
    const showPending =
      notification.displayLifecycle === 'detailed' &&
      (notification.status === 'pending' || notification.status === 'submitted')

    return (
      <div style={{ flex: '0 0 auto' }} ref={this.ref}>
        <NotificationErrorBoundary notificationId={notification.id}>
          {showPending && <PendingInline notification={notification} />}
          {!showPending && (
            <InViewFrame onChange={this.cardVisibilityChanged} root={inViewParent} enabled={!notification.viewed}>
              <StyledAdaptiveCard
                id={notification.id}
                data-id={'wg-ac-card'}
                data-testid={'wg-ac-card'}
                // @ts-expect-error
                priority={priority}
                selected={selected}
                hasActions={!!hasActions}
                hasSelectAction={!!isInteractable(notification) && !!get(notification, 'card.selectAction')}
                onClick={this.onSelectAction}
              >
                {this.isNew && <NewFlag />}
                {!!notification.source && <Header source={notification.source} />}
                {!!card.selectAction && (
                  <NotificationInteractionIndicator
                    // @ts-expect-error
                    selected={selected}
                    type={card.selectAction.type}
                    priority={priority}
                  />
                )}
                {notification.status !== 'error' && loading && this.renderLoading()}
                {notification.status === 'deleted' && this.renderDeleted()}
                <StyledAdaptiveCardBody>
                  {card.body.map((cardElement: any, index: any) => {
                    const CardElement = ComponentRegistry.getComponent(cardElement.type)
                    if (CardElement) {
                      return (
                        // @ts-expect-error
                        <CardElement
                          key={index}
                          cardId={notification.id}
                          {...cardElement}
                          spacing={'none'}
                          separator={false}
                          // @ts-expect-error
                          registerInputCallback={this.registerInputCallback}
                          // @ts-expect-error
                          actionHandler={this.actionHandler}
                          // @ts-expect-error
                          inlineDetailCard={this.state.inlineDetailCard}
                          document={document}
                        />
                      )
                    } else {
                      return <div key={index} />
                    }
                  })}
                </StyledAdaptiveCardBody>
                <Footer
                  // @ts-expect-error
                  onDelete={get(card, 'commonActions.delete') ? this.actionHandler : null}
                  notification={notification}
                  document={document}
                />
                {card.actions && card.actions.length > 0 ? (
                  // @ts-expect-error
                  <ActionSet disabled={disableActions} actions={card.actions} actionHandler={this.actionHandler} />
                ) : (
                  ''
                )}
              </StyledAdaptiveCard>
            </InViewFrame>
          )}
        </NotificationErrorBoundary>
      </div>
    )
  }
}

AdaptiveCard.displayName = 'AdaptiveCard'

AdaptiveCard.defaultProps = {
  // @ts-expect-error
  onCardHasBecomeVisible: () => {},
  selected: false
}

export default AdaptiveCard
